import type { DarkPoolRow } from 'constants/payloads/realtime';

/**
 * Safety bound on dark-pool rows held in the browser. Not a product cap: a full
 * session is ~33,000 prints (32,570 on 2026-10-26), so the whole day fits with
 * room to spare. The old 10,000 cap copied the Hasura query limit and dropped
 * about two thirds of a busy day.
 */
export const DARK_POOL_MAX_ROWS = 100_000;

function tsOf(row: DarkPoolRow): number {
  const t = row.timestamp;
  return typeof t === 'number' ? t : Number(t) || 0;
}

/**
 * Merge new prints into a newest-first list, deduped by seq (the incoming copy
 * wins), capped at `maxRows`.
 *
 * `prev` is already newest-first, so only the small incoming batch is sorted and
 * the two lists are merged in one pass. The old version rebuilt a Map of every
 * held row and re-sorted the whole list on every 500 ms flush. On equal
 * timestamps a held row stays ahead of a new one, as the old stable sort kept it.
 */
export function mergeDarkPoolRows(
  prev: DarkPoolRow[],
  incoming: DarkPoolRow[],
  maxRows = DARK_POOL_MAX_ROWS,
): DarkPoolRow[] {
  if (!incoming.length) return prev;
  const latest = new Map<number, DarkPoolRow>();
  for (const r of incoming) latest.set(r.seq, r);

  const held = prev.map((r) => {
    const replacement = latest.get(r.seq);
    if (!replacement) return r;
    latest.delete(r.seq);
    return replacement;
  });
  const fresh = Array.from(latest.values()).sort((a, b) => tsOf(b) - tsOf(a));

  const out: DarkPoolRow[] = [];
  let i = 0;
  let j = 0;
  while (out.length < maxRows && (i < held.length || j < fresh.length)) {
    if (j >= fresh.length || (i < held.length && tsOf(held[i]) >= tsOf(fresh[j]))) {
      out.push(held[i++]);
    } else {
      out.push(fresh[j++]);
    }
  }
  return out;
}
